import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useContributorsDataGql } from '../../graphql';
import { debounce } from 'lodash';
import { useDebounce } from '@devd-client/devd/components';
import { getContributorEmails } from '../../helpers/Goals.utils';
import { Contributor } from '../../helpers/goals.types';

interface ContributorTableProps {
  onSelectContributors: (contributors: string[]) => void;
  searchTerm?: string;
  teamId?: string;
  initialSelectedContributors?: string[];
  showSelectedOnly?: boolean;
}

const ContributorSelectTable = ({
  onSelectContributors,
  searchTerm = '',
  teamId = '',
  initialSelectedContributors = [],
  showSelectedOnly = false,
}: ContributorTableProps) => {
  const [pageNo, setPageNo] = useState(0);
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [selectedContributors, setSelectedContributors] = useState<string[]>(
    initialSelectedContributors || []
  );
  const tableRef = useRef<HTMLDivElement>(null);

  const { data: membersData, isLoading: membersDataLoading } =
    useContributorsDataGql(
      {
        teamId,
        startDate: null,
        endDate: null,
        contributorFilters: {
          activeFilters: null,
          inActiveFilters: null,
          excluded: 'false',
        },
        searchTerm: debouncedSearch,
      },
      pageNo,
      20
    );

  useEffect(() => {
    if (membersData?.data) {
      setTimeout(() => {
        if (pageNo === 0) {
          setContributors([...membersData.data]);
        } else {
          setContributors((prev) => [...prev, ...membersData.data]);
        }
      }, 0);
    }
  }, [membersData, pageNo]);

  useEffect(() => {
    setPageNo(0);
    setContributors([]);
  }, [debouncedSearch, teamId]);

  useEffect(() => {
    onSelectContributors(selectedContributors);
  }, [selectedContributors, onSelectContributors]);

  const debouncedSetPageNo = useCallback(
    debounce(() => {
      setPageNo((prev) => prev + 1);
    }, 100),
    []
  );

  const handleScroll = () => {
    if (!tableRef.current || membersDataLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
    if (
      scrollTop + clientHeight >= scrollHeight - 20 &&
      membersData?.data?.length &&
      membersData?.data?.length >= 20
    ) {
      debouncedSetPageNo();
    }
  };

  const handleCheckboxChange = (contributor: Contributor) => {
    setSelectedContributors((prev) => {
      const exists = prev.includes(contributor.email);
      if (exists) {
        return prev.filter((c) => c !== contributor.email);
      } else {
        return [...prev, contributor.email];
      }
    });
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (showSelectedOnly) {
      if (!isChecked) {
        setSelectedContributors([]);
      }
      return;
    }
    const currentPageEmails = getContributorEmails(contributors);
    setSelectedContributors((prev) => {
      if (isChecked) {
        const newSelections = currentPageEmails.filter(
          (email) => !prev.includes(email)
        );
        return [...prev, ...newSelections];
      } else {
        return prev.filter((email) => !currentPageEmails.includes(email));
      }
    });
  };

  const currentPageEmails = showSelectedOnly
    ? selectedContributors
    : getContributorEmails(contributors);

  const isAllSelected = showSelectedOnly
    ? selectedContributors.length > 0
    : currentPageEmails.length > 0 &&
      currentPageEmails.every((email) => selectedContributors.includes(email));

  return (
    <Box
      maxH="400px"
      minH="400px"
      overflowY="auto"
      borderWidth="1px"
      borderColor="text.tertiary"
      borderRadius="lg"
      ref={tableRef}
      onScroll={handleScroll}
    >
      {membersDataLoading && pageNo === 0 ? (
        <Table variant="simple" size="sm" bg="white">
          <Thead position="sticky" top={0} bg="#FBFBFB" zIndex={1}>
            <Tr py={1}>
              <Th w="5%" textTransform="none">
                <Checkbox isChecked={false} size="lg" />
              </Th>
              <Th w="30%" textTransform="none">
                Name
              </Th>
              <Th w="35%" textTransform="none">
                Email
              </Th>
              <Th w="30%" textTransform="none">
                Teams
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {[...Array(10)].map((_, index) => (
              <Tr key={index}>
                <Td>
                  <Skeleton height="20px" width="20px" />
                </Td>
                <Td>
                  <Skeleton height="20px" width="90%" />
                </Td>
                <Td>
                  <Skeleton height="20px" width="90%" />
                </Td>
                <Td>
                  <Skeleton height="20px" width="90%" />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Table
          variant="simple"
          size="sm"
          bg="white"
          style={{ tableLayout: 'fixed' }}
        >
          <Thead position="sticky" top={0} bg="#FBFBFB" zIndex={1}>
            <Tr>
              <Th w="5%" textTransform="none">
                <Checkbox
                  isChecked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  size="lg"
                />
              </Th>
              <Th w="30%" textTransform="none">
                Name
              </Th>
              <Th w="35%" textTransform="none">
                Email
              </Th>
              <Th w="30%" textTransform="none">
                Teams
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {showSelectedOnly
              ? selectedContributors.map((email, index) => (
                  <Tr key={`selected-${email}-${index}`}>
                    <Td>
                      <Checkbox
                        isChecked={true}
                        onChange={() => {
                          setSelectedContributors((prev) =>
                            prev.filter((e) => e !== email)
                          );
                        }}
                        size="lg"
                        color={'primary'}
                      />
                    </Td>
                    <Td>
                      <Text fontSize="sm" isTruncated maxW="320px">
                        -
                      </Text>
                    </Td>
                    <Td>
                      <Tooltip label={email}>
                        <Text fontSize="sm" isTruncated maxW="320px">
                          {email}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td>
                      <Text fontSize="sm" isTruncated maxW="320px">
                        -
                      </Text>
                    </Td>
                  </Tr>
                ))
              : contributors?.map((contributor, index) => (
                  <Tr key={`${contributor?.email}-${index}`}>
                    <Td>
                      <Checkbox
                        isChecked={selectedContributors?.includes(
                          contributor?.email
                        )}
                        onChange={() => handleCheckboxChange(contributor)}
                        size="lg"
                        opacity={!contributor?.email ? '0.4' : '1'}
                        isDisabled={!contributor?.email}
                        color={'primary'}
                      />
                    </Td>
                    <Td>
                      <Text fontSize="sm" isTruncated maxW="320px">
                        {contributor?.name || '-'}
                      </Text>
                    </Td>
                    <Td>
                      {contributor?.email ? (
                        <Tooltip label={contributor?.email}>
                          <Text fontSize="sm" isTruncated maxW="320px">
                            {contributor?.email || '-'}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text fontSize="sm" isTruncated maxW="320px">
                          {contributor?.email || '-'}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      {contributor?.teams?.length > 0 ? (
                        <Tooltip label={contributor?.teams?.join(', ')}>
                          <Text fontSize="sm" isTruncated maxW="320px">
                            {contributor?.teams?.join(', ') || '-'}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text fontSize="sm" isTruncated maxW="320px">
                          {contributor?.teams?.join(', ') || '-'}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
            {membersDataLoading &&
              pageNo > 0 &&
              [...Array(3)].map((_, index) => (
                <Tr key={`loading-${index}`} pb={10}>
                  <Td>
                    <Skeleton height="20px" width="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" width="90%" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" width="90%" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" width="90%" />
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};

export default ContributorSelectTable;
